import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useUser } from "../contexts/UserContext";
import Footer from '../components/Footer'
import { motion } from "framer-motion";
import { exportToWord } from "../utils/printUtils";
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from '../utils/printHelper';
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function ResignationLetter() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const printRef = useRef();
  const [letters, setLetters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    companyName: "",
    position: "",
    resignationDate: "",
    lastWorkingDate: "",
    reason: "",
    recipientName: "",
    recipientPosition: "",
    content: "",
    language: "id",
  });

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");

  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage("");
      setToastType("");
    }, 3000);
  };

  useEffect(() => {
    if (userInfo?.id) {
      fetchLetters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const fetchLetters = async () => {
    try {
      setIsLoading(true);
      // Admin dapat melihat semua data, user biasa hanya data mereka sendiri
      let query = supabase
        .from("resignation_letters")
        .select("*");
      
      if (userInfo.role !== 'admin') {
        query = query.eq("user_id", userInfo.id);
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch user info untuk admin
      let dataWithUsers = data;
      if (userInfo.role === 'admin' && data && data.length > 0) {
        const userIds = [...new Set(data.map(item => item.user_id))];
        const { data: usersData } = await supabase
          .from("users")
          .select("id, full_name, email")
          .in("id", userIds);
        
        dataWithUsers = data.map(item => ({
          ...item,
          user: usersData?.find(u => u.id === item.user_id) || null
        }));
      }

      if (error) throw error;

      const transformed = dataWithUsers.map((item) => ({
        id: item.id,
        companyName: item.company_name,
        position: item.position,
        resignationDate: item.resignation_date,
        lastWorkingDate: item.last_working_date,
        reason: item.reason,
        recipientName: item.recipient_name,
        recipientPosition: item.recipient_position,
        content: item.content,
        createdAt: item.created_at,
        user: item.user,
        language: item.language || "id",
      }));

      setLetters(transformed);
    } catch (error) {
      console.error("Error fetching letters:", error);
      showToast("Gagal memuat data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.companyName || !form.position || !form.resignationDate || !form.lastWorkingDate) {
      showToast("Field yang ditandai (*) wajib diisi", "error");
      return;
    }

    if (form.resignationDate > form.lastWorkingDate) {
      showToast("Tanggal pengunduran diri tidak boleh lebih dari hari kerja terakhir", "error");
      return;
    }

    try {
      const payload = {
        user_id: userInfo.id,
        company_name: form.companyName,
        position: form.position,
        resignation_date: form.resignationDate,
        last_working_date: form.lastWorkingDate,
        reason: form.reason,
        recipient_name: form.recipientName,
        recipient_position: form.recipientPosition,
        content: form.content,
        language: form.language || "id",
      };

      if (editingId) {
        const { error } = await supabase
          .from("resignation_letters")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
        showToast("Berhasil mengupdate surat resign");
      } else {
        const { error } = await supabase
          .from("resignation_letters")
          .insert([payload]);

        if (error) throw error;
        showToast("Berhasil membuat surat resign");
      }

      fetchLetters();
      closeModal();
    } catch (error) {
      console.error("Error:", error);
      showToast(error.message || "Gagal menyimpan data", "error");
    }
  };

  const handleEdit = (letter) => {
    setForm({
      companyName: letter.companyName,
      position: letter.position,
      resignationDate: letter.resignationDate,
      lastWorkingDate: letter.lastWorkingDate,
      reason: letter.reason,
      recipientName: letter.recipientName,
      recipientPosition: letter.recipientPosition,
      content: letter.content,
      language: letter.language || "id",
    });
    setEditingId(letter.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus surat resign ini?")) return;

    try {
      const { error } = await supabase
        .from("resignation_letters")
        .delete()
        .eq("id", id);

      if (error) throw error;

      showToast("Berhasil menghapus surat resign");
      fetchLetters();
    } catch (error) {
      console.error("Error:", error);
      showToast("Gagal menghapus data", "error");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({
      companyName: "",
      position: "",
      resignationDate: "",
      lastWorkingDate: "",
      reason: "",
      recipientName: "",
      recipientPosition: "",
      content: "",
      language: "id",
    });
  };

  const generateLetterContent = (letter) => {
    if (letter.content && letter.content.trim()) {
      return (
        <div style={{ whiteSpace: 'pre-line' }}>
          {letter.content}
        </div>
      );
    }

    const lang = letter.language || 'id';
    
    // Format tanggal berdasarkan bahasa
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const locales = {
        id: 'id-ID',
        en: 'en-US',
        ja: 'ja-JP',
        ko: 'ko-KR',
        zh: 'zh-CN'
      };
      return date.toLocaleDateString(locales[lang] || 'id-ID', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    };

    // Template multi bahasa
    const templates = {
      id: {
        intro: 'Melalui surat ini, saya yang bertanda tangan di bawah ini:',
        name: 'Nama',
        position: 'Posisi',
        address: 'Alamat',
        phone: 'Telepon',
        email: 'Email',
        para1: `Dengan ini mengajukan pengunduran diri dari <strong>${letter.companyName}</strong>, terhitung sejak tanggal <strong>${formatDate(letter.resignationDate)}</strong>.`,
        para2_start: `Hari kerja terakhir saya akan berakhir pada tanggal <strong>${formatDate(letter.lastWorkingDate)}</strong>.`,
        para2_reason: letter.reason ? ` Alasan pengunduran diri saya adalah ${letter.reason}.` : '',
        para3: `Saya sangat berterima kasih atas kesempatan yang telah diberikan untuk bekerja dan berkembang di ${letter.companyName}. Selama bekerja di perusahaan ini, saya telah memperoleh banyak pengalaman dan ilmu yang sangat berharga.`,
        para4: `Saya berkomitmen untuk menyelesaikan semua tugas dan tanggung jawab saya dengan baik hingga hari kerja terakhir, serta membantu proses transisi pekerjaan kepada pengganti saya dengan lancar.`,
        para5: `Saya mohon maaf apabila ada kesalahan atau kekurangan selama bekerja di perusahaan. Semoga ${letter.companyName} terus berkembang dan sukses di masa mendatang.`
      },
      en: {
        intro: 'Through this letter, I, the undersigned:',
        name: 'Name',
        position: 'Position',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        para1: `I hereby submit my resignation from <strong>${letter.companyName}</strong>, effective from <strong>${formatDate(letter.resignationDate)}</strong>.`,
        para2_start: `My last working day will be on <strong>${formatDate(letter.lastWorkingDate)}</strong>.`,
        para2_reason: letter.reason ? ` The reason for my resignation is ${letter.reason}.` : '',
        para3: `I am very grateful for the opportunity to work and develop at ${letter.companyName}. During my time at this company, I have gained many valuable experiences and knowledge.`,
        para4: `I am committed to completing all my tasks and responsibilities well until my last working day, as well as helping the transition process to my replacement smoothly.`,
        para5: `I apologize if there were any mistakes or shortcomings during my work at the company. May ${letter.companyName} continue to grow and succeed in the future.`
      },
      ja: {
        intro: 'この手紙を通じて、私、以下の署名者は：',
        name: '氏名',
        position: '役職',
        address: '住所',
        phone: '電話番号',
        email: 'メール',
        para1: `<strong>${formatDate(letter.resignationDate)}</strong>付けで、<strong>${letter.companyName}</strong>を退職させていただきたく、お願い申し上げます。`,
        para2_start: `最終出勤日は<strong>${formatDate(letter.lastWorkingDate)}</strong>となります。`,
        para2_reason: letter.reason ? ` 退職の理由は${letter.reason}です。` : '',
        para3: `${letter.companyName}で働き、成長する機会を与えていただいたことに大変感謝しております。この会社で働いている間、多くの貴重な経験と知識を得ることができました。`,
        para4: `最終出勤日まで、すべての業務と責任を適切に完了し、後任者への引き継ぎをスムーズに進めることをお約束いたします。`,
        para5: `在職中に何か過ちや不足があった場合は、お詫び申し上げます。${letter.companyName}が今後も発展し、成功されることを願っております。`
      },
      ko: {
        intro: '이 편지를 통해, 저 아래 서명한 자는:',
        name: '이름',
        position: '직책',
        address: '주소',
        phone: '전화',
        email: '이메일',
        para1: `<strong>${formatDate(letter.resignationDate)}</strong>부로 <strong>${letter.companyName}</strong>에서 사직을 제출합니다.`,
        para2_start: `마지막 근무일은 <strong>${formatDate(letter.lastWorkingDate)}</strong>입니다.`,
        para2_reason: letter.reason ? ` 사직 사유는 ${letter.reason}입니다.` : '',
        para3: `${letter.companyName}에서 일하고 성장할 수 있는 기회를 주신 것에 대해 매우 감사드립니다. 이 회사에서 근무하는 동안 많은 귀중한 경험과 지식을 얻었습니다.`,
        para4: `마지막 근무일까지 모든 업무와 책임을 잘 완수하고, 후임자에게 원활하게 인수인계를 도와드릴 것을 약속드립니다.`,
        para5: `재직 중 실수나 부족한 점이 있었다면 사과드립니다. ${letter.companyName}이 앞으로도 계속 발전하고 성공하기를 기원합니다.`
      },
      zh: {
        intro: '通过这封信，我，下方签署人：',
        name: '姓名',
        position: '职位',
        address: '地址',
        phone: '电话',
        email: '邮箱',
        para1: `特此提交从<strong>${letter.companyName}</strong>辞职的申请，自<strong>${formatDate(letter.resignationDate)}</strong>起生效。`,
        para2_start: `我的最后工作日将是<strong>${formatDate(letter.lastWorkingDate)}</strong>。`,
        para2_reason: letter.reason ? ` 辞职原因是${letter.reason}。` : '',
        para3: `非常感谢您给予我在${letter.companyName}工作和发展的机会。在这家公司工作期间，我获得了许多宝贵的经验和知识。`,
        para4: `我承诺在最后工作日之前完成所有任务和职责，并协助顺利过渡给我的继任者。`,
        para5: `如果在公司工作期间有任何错误或不足之处，我深表歉意。祝${letter.companyName}未来继续发展和成功。`
      }
    };

    const t = templates[lang] || templates.id;

    return (
      <div>
        <p style={{ margin: '0 0 15px 0' }}>
          {t.intro}
        </p>
        
        <div style={{ marginLeft: '0', marginBottom: '20px', lineHeight: '2' }}>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.name}</span>
            <span>: {userInfo.full_name || '-'}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.position}</span>
            <span>: {letter.position}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.address}</span>
            <span>: {userInfo.location || '-'}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.phone}</span>
            <span>: {userInfo.phone || '-'}</span>
          </p>
          <p style={{ margin: '3px 0' }}>
            <span style={{ display: 'inline-block', width: '100px' }}>{t.email}</span>
            <span>: {userInfo.email || '-'}</span>
          </p>
        </div>

        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para1 }} />
        <p style={{ margin: '15px 0' }}>
          <span dangerouslySetInnerHTML={{ __html: t.para2_start }} />
          {t.para2_reason}
        </p>
        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para3 }} />
        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para4 }} />
        <p style={{ margin: '15px 0' }} dangerouslySetInnerHTML={{ __html: t.para5 }} />
      </div>
    );
  };

  const handlePreview = (letter) => {
    setPreviewData(letter);
    setShowPreview(true);
  };

  const handlePrint = () => {
    if (isMobileDevice()) {
      showToast('💡 Tip: Untuk hasil terbaik di mobile, gunakan Export ke Word', 'info');
      setTimeout(() => {
        handleMobileFriendlyPrint({
          documentTitle: `Surat_Resign_${previewData?.companyName || 'Perusahaan'}`,
          onAfterPrint: () => showToast('Dokumen berhasil dicetak')
        });
      }, 1000);
    } else {
      handleDesktopPrint();
    }
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Surat_Resign_${previewData?.companyName || 'Perusahaan'}`,
    onAfterPrint: () => {
      showToast('Dokumen berhasil dicetak');
    }
  });

  const handleExportWord = () => {
    exportToWord('printable-letter', `Surat_Resign_${previewData?.companyName || 'Perusahaan'}_${new Date().getTime()}`);
    showToast('Dokumen berhasil diexport ke Word');
  };

  // Helper untuk mendapatkan teks multi bahasa
  const getLetterTexts = (lang) => {
    const texts = {
      id: {
        title: 'SURAT PENGUNDURAN DIRI',
        numberPrefix: 'Nomor:',
        numberSuffix: '/RESIGN/',
        to: 'Kepada Yth.',
        defaultRecipient: 'Bapak/Ibu Pimpinan',
        atPlace: 'Di tempat',
        subject: 'Perihal:',
        subjectText: 'Pengunduran Diri',
        greeting: 'Dengan hormat,',
        closing: 'Demikian surat pengunduran diri ini saya buat dengan sebenar-benarnya. Atas perhatian dan pengertian Bapak/Ibu, saya ucapkan terima kasih.',
        regards: 'Hormat saya,'
      },
      en: {
        title: 'RESIGNATION LETTER',
        numberPrefix: 'Reference:',
        numberSuffix: '/RESIGN/',
        to: 'Dear',
        defaultRecipient: 'Sir/Madam Director',
        atPlace: 'Present',
        subject: 'Subject:',
        subjectText: 'Resignation',
        greeting: 'Dear Sir/Madam,',
        closing: 'I have written this resignation letter sincerely. I thank you for your attention and understanding.',
        regards: 'Sincerely yours,'
      },
      ja: {
        title: '退職届',
        numberPrefix: '文書番号：',
        numberSuffix: '/退職/',
        to: '拝啓',
        defaultRecipient: '社長殿',
        atPlace: '御中',
        subject: '件名：',
        subjectText: '退職の件',
        greeting: '拝啓',
        closing: 'この退職届を誠実に作成いたしました。ご配慮とご理解に感謝申し上げます。',
        regards: '敬具'
      },
      ko: {
        title: '사직서',
        numberPrefix: '문서번호:',
        numberSuffix: '/사직/',
        to: '귀하',
        defaultRecipient: '사장님',
        atPlace: '귀하',
        subject: '제목:',
        subjectText: '사직',
        greeting: '귀하께,',
        closing: '이 사직서를 성실하게 작성하였습니다. 관심과 이해에 감사드립니다.',
        regards: '감사합니다,'
      },
      zh: {
        title: '辞职信',
        numberPrefix: '编号：',
        numberSuffix: '/辞职/',
        to: '敬启者：',
        defaultRecipient: '总经理',
        atPlace: '敬启',
        subject: '主题：',
        subjectText: '辞职',
        greeting: '尊敬的先生/女士：',
        closing: '本人真诚地撰写了这封辞职信。感谢您的关注和理解。',
        regards: '此致敬礼'
      }
    };
    return texts[lang] || texts.id;
  };

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #printable-letter,
          #printable-letter * {
            visibility: visible !important;
          }
          #printable-letter {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>
      <div className="min-h-screen bg-white relative overflow-hidden no-print flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 relative">
                  <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-rose-500 border-r-pink-500 animate-spin"></div>
                  <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-rose-500 animate-spin-reverse"></div>
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-rose-400 to-pink-600 animate-pulse shadow-lg"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading resignation letters...</p>
            </div>
          </div>
        ) : (
          <>
        {/* Soft Futuristic Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 via-pink-100/30 to-rose-100/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-rose-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-50/20 via-blue-50/20 to-pink-50/20 rounded-full blur-3xl animate-float-slow"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(99, 102, 241, 0.02) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
      <div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
              <div className="relative backdrop-blur-xl bg-gradient-to-r from-rose-600/90 via-pink-600/90 to-blue-600/90 p-6 sm:p-8 text-white rounded-t-3xl shadow-xl border border-white/20 overflow-hidden">
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer pointer-events-none"></div>
                
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 animate-pulse"></div>

                <div className="relative z-10">
                  <div className="flex justify-between items-center flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all duration-200 group "
                        title="Back to Dashboard"
                      >
                        <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                      </button>
                      <div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
                          Surat Pengunduran Diri (Resign)
                        </h1>
                        <p className="text-red-100 text-xs sm:text-sm mt-1 flex items-center gap-2">
                          <DocumentTextIcon className="w-4 h-4" />
                          <span>Buat dan kelola surat pengunduran diri</span>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(true)}
                      className="flex items-center gap-2 bg-white hover:bg-red-50 text-red-700 px-4 py-2.5 rounded-xl shadow-sm font-medium transition-all duration-200 "
                    >
                      <PlusIcon className="w-5 h-5" />
                      <span>Buat Baru</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-b-2xl shadow-xl p-6 sm:p-8">
                {/* Table - Desktop */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-red-50 to-orange-50">
                      <tr>
                        {userInfo?.role === 'admin' && (
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dibuat Oleh</th>
                        )}
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Perusahaan</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Posisi</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanggal Resign</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Hari Kerja Terakhir</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Alasan</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {letters.length === 0 ? (
                        <tr>
                          <td colSpan={userInfo?.role === 'admin' ? "7" : "6"} className="px-4 py-8 text-center text-slate-500">
                            Belum ada surat pengunduran diri
                          </td>
                        </tr>
                      ) : (
                        letters.map((letter) => (
                          <tr key={letter.id} className="hover:bg-gray-50 transition-colors">
                            {userInfo?.role === 'admin' && (
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-semibold text-sm text-slate-800">{letter.user?.full_name || 'N/A'}</p>
                                  <p className="text-xs text-slate-500">{letter.user?.email || ''}</p>
                                </div>
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{letter.companyName}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">{letter.position}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {new Date(letter.resignationDate).toLocaleDateString("id-ID")}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">
                              {new Date(letter.lastWorkingDate).toLocaleDateString("id-ID")}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">
                              {letter.reason || "-"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handlePreview(letter)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Preview & Print"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEdit(letter)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(letter.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Hapus"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Cards - Mobile */}
                <div className="md:hidden space-y-4">
                  {letters.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Belum ada surat pengunduran diri
                    </div>
                  ) : (
                    letters.map((letter) => (
                      <div
                        key={letter.id}
                        className="bg-gradient-to-br from-slate-50 to-red-50 p-4 rounded-xl border border-gray-200 shadow-sm"
                      >
                        {userInfo?.role === 'admin' && letter.user && (
                          <div className="pb-3 mb-3 border-b border-gray-200">
                            <p className="text-xs text-slate-500 mb-1">Dibuat Oleh:</p>
                            <p className="font-semibold text-sm text-slate-800">{letter.user.full_name}</p>
                            <p className="text-xs text-slate-500">{letter.user.email}</p>
                          </div>
                        )}
                        <div className="space-y-2 mb-3">
                          <h3 className="font-bold text-gray-900">{letter.companyName}</h3>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Posisi:</span> {letter.position}
                          </p>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Tanggal Resign:</span>{" "}
                            {new Date(letter.resignationDate).toLocaleDateString("id-ID")}
                          </p>
                          <p className="text-sm text-slate-600">
                            <span className="font-semibold">Hari Kerja Terakhir:</span>{" "}
                            {new Date(letter.lastWorkingDate).toLocaleDateString("id-ID")}
                          </p>
                          {letter.reason && (
                            <p className="text-sm text-slate-600">
                              <span className="font-semibold">Alasan:</span> {letter.reason}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => handlePreview(letter)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 bg-green-600 hover:bg-green-700 active:scale-95 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow-md transition-all min-h-[40px]"
                          >
                            <EyeIcon className="w-4 h-4" />
                            <span>Preview</span>
                          </button>
                          <button
                            onClick={() => handleEdit(letter)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow-md transition-all min-h-[40px]"
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(letter.id)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white rounded-lg text-xs font-bold shadow-sm hover:shadow-md transition-all min-h-[40px]"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Hapus</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>

      {/* Modal */}
      {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[75vh] flex flex-col p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-3 sm:mb-4 flex-shrink-0">
                {editingId ? "Edit Surat Resign" : "Buat Surat Resign Baru"}
              </h2>

              <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Nama Perusahaan <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={form.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="PT. Contoh Indonesia"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Posisi Saat Ini <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={form.position}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Software Developer"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Tanggal Resign <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        name="resignationDate"
                        value={form.resignationDate}
                        onChange={handleChange}
                        className="w-full pl-3 sm:pl-10 pr-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[44px] bg-white"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Hari Kerja Terakhir <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        name="lastWorkingDate"
                        value={form.lastWorkingDate}
                        onChange={handleChange}
                        className="w-full pl-3 sm:pl-10 pr-3 py-2.5 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[44px] bg-white"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Alasan Resign</label>
                  <input
                    type="text"
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Mencari tantangan baru, dll"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Penerima</label>
                    <input
                      type="text"
                      name="recipientName"
                      value={form.recipientName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Bapak/Ibu Manager"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Jabatan Penerima</label>
                    <input
                      type="text"
                      name="recipientPosition"
                      value={form.recipientPosition}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Manager HRD"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    🌐 Bahasa Surat <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="language"
                    value={form.language}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  >
                    <option value="id">🇮🇩 Bahasa Indonesia</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="ja">🇯🇵 日本語 (Japanese)</option>
                    <option value="ko">🇰🇷 한국어 (Korean)</option>
                    <option value="zh">🇨🇳 中文 (Chinese)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Pilih bahasa untuk template surat otomatis
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Isi Surat (Opsional)</label>
                  <textarea
                    name="content"
                    value={form.content}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Dengan hormat, melalui surat ini saya mengajukan pengunduran diri..."
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">
                    Jika kosong, sistem akan menggunakan template default
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl font-semibold text-slate-700 hover:bg-gray-50 active:scale-95 transition-all min-h-[48px]"
                >
                  Batal
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 active:scale-95 disabled:bg-gray-400 text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all min-h-[48px]"
                >
                  {isLoading ? "Menyimpan..." : editingId ? "Update" : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview & Print Modal */}
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex justify-center items-center z-[10000] p-4 print:hidden">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[75vh] rounded-xl sm:rounded-2xl shadow-2xl flex flex-col relative print:max-h-none print:max-w-none print:shadow-none">
              {/* Header - No Print */}
              <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 print:hidden">
                <h2 className="text-xl font-bold text-slate-800 text-center sm:text-left mb-2 sm:mb-0">Preview Surat Resign</h2>
                
                {/* Mobile Hint */}
                <div className="block sm:hidden bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2 text-xs text-amber-800">
                  📱 <strong>Tip:</strong> Jika Print blank, gunakan <strong>Export Word</strong>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 justify-end w-full sm:w-auto">
                  <button
                    onClick={handleExportWord}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 active:scale-95 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all min-h-[44px] w-full sm:w-auto"
                    title="Export ke Word"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Export Word</span>
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all min-h-[44px] w-full sm:w-auto"
                  >
                    <DocumentArrowDownIcon className="w-5 h-5" />
                    <span>Print</span>
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2.5 border-2 border-gray-300 rounded-xl font-semibold text-slate-700 hover:bg-gray-50 active:scale-95 transition-all min-h-[44px] w-full sm:w-auto"
                  >
                    Tutup
                  </button>
                </div>
              </div>

              {/* Letter Content - Printable */}
              <div className="flex-1 overflow-y-auto p-8 print:p-0 print:overflow-visible print:h-auto">
                <div ref={printRef} className="printable-document document-container" id="printable-letter">
                  {/* A4 Paper Style */}
                  <div className="document-page">
                    {(() => {
                      const lang = previewData.language || 'id';
                      const texts = getLetterTexts(lang);
                      const locales = {
                        id: 'id-ID',
                        en: 'en-US',
                        ja: 'ja-JP',
                        ko: 'ko-KR',
                        zh: 'zh-CN'
                      };
                      const locale = locales[lang] || 'id-ID';

                      return (
                        <>
                          {/* Header Kop Surat */}
                          <div className="document-header" style={{ marginBottom: '25px' }}>
                            <h1 className="document-title" style={{ fontSize: '14pt', fontWeight: 'bold', textAlign: 'center', textDecoration: 'underline', textUnderlineOffset: '3pt', letterSpacing: '1px', marginBottom: '0' }}>
                              {texts.title}
                            </h1>
                          </div>

                          {/* Nomor Surat (opsional) */}
                          <div className="reference-number" style={{ textAlign: 'center', marginBottom: '25px', fontSize: '11pt', fontWeight: '600' }}>
                            {texts.numberPrefix} {previewData.id?.substring(0, 8).toUpperCase()}{texts.numberSuffix}{new Date().getFullYear()}
                          </div>

                          {/* Tempat dan Tanggal */}
                          <div className="document-date" style={{ textAlign: 'right', marginBottom: '25px', fontSize: '11pt' }}>
                            <p style={{ margin: 0 }}>
                              {userInfo.location || (lang === 'en' ? 'Jakarta' : lang === 'ja' ? 'ジャカルタ' : lang === 'ko' ? '자카르타' : lang === 'zh' ? '雅加达' : 'Jakarta')}, {new Date(previewData.resignationDate).toLocaleDateString(locale, { 
                                day: 'numeric',
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                          </div>

                          {/* Penerima */}
                          <div className="document-recipient" style={{ marginBottom: '25px', lineHeight: '1.6' }}>
                            <p style={{ margin: '3px 0' }}>{texts.to}</p>
                            <p style={{ margin: '3px 0', fontWeight: 'bold' }}>
                              {previewData.recipientName || texts.defaultRecipient}
                            </p>
                            {previewData.recipientPosition && (
                              <p style={{ margin: '3px 0' }}>{previewData.recipientPosition}</p>
                            )}
                            <p style={{ margin: '8px 0 3px 0', fontWeight: 'bold' }}>{previewData.companyName}</p>
                            <p style={{ margin: '3px 0' }}>{texts.atPlace}</p>
                          </div>
                        </>
                      );
                    })()}

                    {/* Perihal */}
                    {(() => {
                      const lang = previewData.language || 'id';
                      const texts = getLetterTexts(lang);

                      return (
                        <>
                          <div style={{ marginBottom: '25px' }}>
                            <p style={{ margin: 0, lineHeight: '1.6' }}>
                              <span style={{ fontWeight: 'bold' }}>{texts.subject}</span> {texts.subjectText}
                            </p>
                          </div>

                          {/* Salam Pembuka */}
                          <div className="document-greeting" style={{ marginBottom: '20px' }}>
                            <p style={{ margin: 0 }}>{texts.greeting}</p>
                          </div>

                          {/* Isi Surat */}
                          <div className="document-body" style={{ marginBottom: '30px', textAlign: 'justify', lineHeight: '1.8' }}>
                            {generateLetterContent(previewData)}
                          </div>

                          {/* Penutup */}
                          <div style={{ marginBottom: '30px', textAlign: 'justify', lineHeight: '1.8' }}>
                            <p style={{ margin: '10px 0' }}>
                              {texts.closing}
                            </p>
                          </div>

                          {/* Signature */}
                          <div className="signature-section" style={{ marginTop: '40px' }}>
                            <div className="document-closing">
                              <p style={{ margin: '0 0 70px 0' }}>{texts.regards}</p>
                              <div>
                                <p style={{ margin: '5px 0', fontWeight: 'bold', textDecoration: 'underline' }}>{userInfo.full_name}</p>
                                <p style={{ margin: '3px 0', fontSize: '10pt', color: '#333' }}>NIK/ID: {userInfo.id?.substring(0, 8).toUpperCase()}</p>
                                {userInfo.phone && (
                                  <p style={{ margin: '3px 0', fontSize: '10pt' }}>{lang === 'en' ? 'Tel' : lang === 'ja' ? '電話' : lang === 'ko' ? '전화' : lang === 'zh' ? '电话' : 'Telp'}: {userInfo.phone}</p>
                                )}
                                {userInfo.email && (
                                  <p style={{ margin: '3px 0', fontSize: '10pt' }}>Email: {userInfo.email}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Tembusan */}
                          <div className="attachments" style={{ marginTop: '35px' }}>
                            <p className="attachments-title" style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '11pt' }}>
                              {lang === 'en' ? 'Copy to:' : lang === 'ja' ? '写し：' : lang === 'ko' ? '사본 수신:' : lang === 'zh' ? '抄送：' : 'Tembusan:'}
                            </p>
                            <ol className="attachments-list" style={{ paddingLeft: '25px', lineHeight: '1.6' }}>
                              <li style={{ margin: '5px 0' }}>
                                {lang === 'en' ? 'HRD Department' : lang === 'ja' ? '人事部' : lang === 'ko' ? 'HRD 부서' : lang === 'zh' ? '人力资源部' : 'Departemen HRD'}
                              </li>
                              <li style={{ margin: '5px 0' }}>
                                {lang === 'en' ? 'Direct Supervisor' : lang === 'ja' ? '直属上司' : lang === 'ko' ? '직속 상사' : lang === 'zh' ? '直接主管' : 'Atasan Langsung'}
                              </li>
                              <li style={{ margin: '5px 0' }}>
                                {lang === 'en' ? 'Personal File' : lang === 'ja' ? '個人ファイル' : lang === 'ko' ? '개인 파일' : lang === 'zh' ? '个人档案' : 'File Pribadi'}
                              </li>
                            </ol>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toastMessage && (
          <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
            <div
              className={`px-6 py-3 rounded-lg shadow-sm ${
                toastType === "error"
                  ? "bg-red-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              {toastMessage}
            </div>
          </div>
        )}
      </div>
      </main>
      <Footer />
          </>
        )}
    </div>
    </>
  );
}
